import React, { useState } from "react";
import API_BASE from "../config/api";
import { adminFetch } from "../utils/adminAuth";

/*
  CR U4 — "Transfer listing ownership: Currently, when an Agent registers
  multiple businesses on behalf of clients, those listings stay under the
  Agent's account and the actual business owner cannot access them."

  Flow: search for the listing (by title/mobile/listinguid) -> see who
  currently owns it and which agent onboarded it -> search for the real
  owner's user account (by mobile/email/name) -> confirm the transfer.
  Every transfer is logged to listing_ownership_transfers for an audit trail.
*/
export default function AdminTransferOwnership() {
    const [listingQuery, setListingQuery] = useState("");
    const [listings, setListings] = useState([]);
    const [selectedListing, setSelectedListing] = useState(null);

    const [userQuery, setUserQuery] = useState("");
    const [users, setUsers] = useState([]);
    const [selectedUser, setSelectedUser] = useState(null);

    const [saving, setSaving] = useState(false);
    const [msg, setMsg] = useState(null);

    const searchListings = async (q) => {
        setListingQuery(q);
        setSelectedListing(null);
        if (q.trim().length < 2) { setListings([]); return; }
        const res = await adminFetch(`${API_BASE}/api/search_listing_for_transfer.php?q=${encodeURIComponent(q)}`);
        const data = await res.json();
        if (data.success) setListings(data.listings);
    };

    const searchUsers = async (q) => {
        setUserQuery(q);
        setSelectedUser(null);
        if (q.trim().length < 2) { setUsers([]); return; }
        const res = await adminFetch(`${API_BASE}/api/search_user_for_transfer.php?q=${encodeURIComponent(q)}`);
        const data = await res.json();
        if (data.success) setUsers(data.users);
    };

    const doTransfer = async () => {
        if (!selectedListing || !selectedUser) return;
        setSaving(true);
        setMsg(null);
        try {
            const res = await adminFetch(`${API_BASE}/api/transfer_listing_ownership.php`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ listinguid: selectedListing.listinguid, new_personaluid: selectedUser.personaluid }),
            });
            const data = await res.json();
            setSaving(false);
            if (data.success) {
                setMsg({ type: "success", text: `"${selectedListing.title}" is now owned by ${selectedUser.fullname}.` });
                setSelectedListing(null); setSelectedUser(null); setListings([]); setUsers([]); setListingQuery(""); setUserQuery("");
            } else {
                setMsg({ type: "danger", text: data.message || "Could not transfer." });
            }
        } catch {
            setSaving(false);
            setMsg({ type: "danger", text: "Network error." });
        }
    };

    return (
        <div>
            <h5 className="fw-bold mb-1">Transfer Listing Ownership</h5>
            <p className="text-secondary small mb-3">
                Move a listing from an Agent's account to the actual business owner's own account.
            </p>

            {msg && <div className={`alert alert-${msg.type} py-2`}>{msg.text}</div>}

            <div className="card shadow-sm mb-3">
                <div className="card-header bg-light fw-semibold">1. Find the Listing</div>
                <div className="card-body">
                    <input
                        className="form-control form-control-sm mb-2"
                        placeholder="Search by business name, mobile, or listing ID..."
                        value={listingQuery}
                        onChange={(e) => searchListings(e.target.value)}
                    />
                    {listings.length > 0 && !selectedListing && (
                        <div className="list-group">
                            {listings.map((l) => (
                                <button key={l.listinguid} className="list-group-item list-group-item-action" onClick={() => setSelectedListing(l)}>
                                    <b>{l.title}</b> — {l.mobile}
                                    <div className="small text-secondary">
                                        Current owner: {l.owner_name || "(unset)"} {l.owner_email ? `· ${l.owner_email}` : ""}
                                        {l.agent_fname && ` · Onboarded by agent: ${l.agent_fname} ${l.agent_lname || ""}`}
                                    </div>
                                </button>
                            ))}
                        </div>
                    )}
                    {selectedListing && (
                        <div className="alert alert-secondary py-2 mb-0 d-flex justify-content-between align-items-center">
                            <div>
                                <b>{selectedListing.title}</b> — {selectedListing.mobile}
                                <div className="small">
                                    Current owner: {selectedListing.owner_name || "(unset)"} {selectedListing.owner_email ? `· ${selectedListing.owner_email}` : ""}
                                </div>
                            </div>
                            <button className="btn btn-sm btn-outline-secondary" onClick={() => setSelectedListing(null)}>Change</button>
                        </div>
                    )}
                </div>
            </div>

            <div className="card shadow-sm mb-3">
                <div className="card-header bg-light fw-semibold">2. Find the Real Owner's Account</div>
                <div className="card-body">
                    <input
                        className="form-control form-control-sm mb-2"
                        placeholder="Search by name, mobile, or email..."
                        value={userQuery}
                        onChange={(e) => searchUsers(e.target.value)}
                    />
                    {users.length > 0 && !selectedUser && (
                        <div className="list-group">
                            {users.map((u) => (
                                <button key={u.personaluid} className="list-group-item list-group-item-action" onClick={() => setSelectedUser(u)}>
                                    <b>{u.fullname}</b> — {u.mobile} · {u.email}
                                </button>
                            ))}
                        </div>
                    )}
                    {selectedUser && (
                        <div className="alert alert-secondary py-2 mb-0 d-flex justify-content-between align-items-center">
                            <div><b>{selectedUser.fullname}</b> — {selectedUser.mobile} · {selectedUser.email}</div>
                            <button className="btn btn-sm btn-outline-secondary" onClick={() => setSelectedUser(null)}>Change</button>
                        </div>
                    )}
                </div>
            </div>

            <div className="d-flex justify-content-end">
                <button
                    className="btn btn-primary px-4"
                    disabled={!selectedListing || !selectedUser || saving}
                    onClick={doTransfer}
                >
                    {saving ? "Transferring..." : "Transfer Ownership"}
                </button>
            </div>
        </div>
    );
}
